import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  b2bReceivableKeys,
  type B2BCreditLimitsQuery,
  type B2BOverdueAlertsQuery,
  type B2BReceivableQuery,
  type B2BReceivableScopeQuery,
} from '@/services/queryKeys';
import { b2bReceivableService, type B2BOverdueAlert, type B2BCreditLimit, type B2BReceivableOverview, type B2BReceivableStaffOption } from '@/services/b2b-receivable.service';

const b2bNavigationQueryOptions = {
  staleTime: 0,
  refetchOnMount: 'always' as const,
};

export function useB2BReceivableList(query: B2BReceivableQuery) {
  return useQuery({
    queryKey: b2bReceivableKeys.list(query),
    queryFn: () => b2bReceivableService.list(query),
    placeholderData: (prev) => prev,
    ...b2bNavigationQueryOptions,
  });
}

export function useB2BReceivableOverview(filters: B2BReceivableScopeQuery = {}) {
  return useQuery<B2BReceivableOverview>({
    queryKey: b2bReceivableKeys.overview(filters),
    queryFn: () => b2bReceivableService.overview(filters),
    ...b2bNavigationQueryOptions,
  });
}

export function useB2BReceivableDashboard(filters: B2BReceivableScopeQuery = {}) {
  return useQuery({
    queryKey: b2bReceivableKeys.dashboard(filters),
    queryFn: () => b2bReceivableService.dashboard(filters),
    ...b2bNavigationQueryOptions,
  });
}

export function useB2BReceivableAging(filters: B2BReceivableScopeQuery = {}) {
  return useQuery({
    queryKey: b2bReceivableKeys.aging(filters),
    queryFn: () => b2bReceivableService.aging(filters),
    ...b2bNavigationQueryOptions,
  });
}

/** Invalidate AR queries (org-order-linked sales invoices). */
export function useRefreshB2BReceivables() {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: b2bReceivableKeys.all });
  };
}

export function useB2BOverdueAlerts(filters: B2BOverdueAlertsQuery = {}) {
  return useQuery<B2BOverdueAlert[]>({
    queryKey: b2bReceivableKeys.overdueAlerts(filters),
    queryFn: () => b2bReceivableService.overdueAlerts(filters),
    ...b2bNavigationQueryOptions,
  });
}

export function useCreateB2BDebtReminder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, note }: { customerId: string; note: string }) =>
      b2bReceivableService.createDebtReminder(customerId, note),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: b2bReceivableKeys.all });
      queryClient.invalidateQueries({ queryKey: b2bReceivableKeys.debtReminders(variables.customerId) });
      queryClient.invalidateQueries({
        queryKey: ['b2b-crm', 'customer-360', variables.customerId],
      });
    },
  });
}

export function useB2BDebtReminders(customerId: string) {
  return useQuery({
    queryKey: b2bReceivableKeys.debtReminders(customerId),
    queryFn: () => b2bReceivableService.debtReminders(customerId),
    enabled: Boolean(customerId),
    ...b2bNavigationQueryOptions,
  });
}

export function useB2BReceivableStaffOptions(enabled = true) {
  return useQuery<B2BReceivableStaffOption[]>({
    queryKey: b2bReceivableKeys.staffOptions(),
    queryFn: b2bReceivableService.staffOptions,
    enabled,
    ...b2bNavigationQueryOptions,
  });
}

export function useB2BCreditLimit(customerId: string | null) {
  return useQuery<B2BCreditLimit>({
    queryKey: b2bReceivableKeys.creditLimit(customerId || ''),
    queryFn: () => b2bReceivableService.creditLimit(customerId!),
    enabled: Boolean(customerId),
    ...b2bNavigationQueryOptions,
  });
}

/**
 * TCR-2439 — hạn mức hiện tại của nhiều khách cùng lúc (panel xác nhận hàng loạt), chung cache với
 * `useB2BCreditLimit`. Chỉ trả khách đã tải xong; khách lỗi (vd không có quyền xem công nợ) bị bỏ qua.
 */
export function useB2BCreditLimitsOf(customerIds: string[]): Record<string, B2BCreditLimit> {
  return useQueries({
    queries: customerIds.map((customerId) => ({
      queryKey: b2bReceivableKeys.creditLimit(customerId),
      queryFn: () => b2bReceivableService.creditLimit(customerId),
      ...b2bNavigationQueryOptions,
    })),
    combine: (results) => {
      const byCustomer: Record<string, B2BCreditLimit> = {};
      results.forEach((result, index) => {
        if (result.data) byCustomer[customerIds[index]] = result.data;
      });
      return byCustomer;
    },
  });
}

/** Bảng hạn mức nợ toàn bộ khách active (TCR-461). */
export function useB2BCreditLimits(query: B2BCreditLimitsQuery = {}) {
  return useQuery({
    queryKey: b2bReceivableKeys.creditLimits(query),
    queryFn: () => b2bReceivableService.creditLimits(query),
    ...b2bNavigationQueryOptions,
  });
}

export function useB2BCustomerReceivables(customerId: string | null) {
  return useQuery({
    queryKey: b2bReceivableKeys.customerReceivables(customerId || ''),
    queryFn: () => b2bReceivableService.listByCustomer(customerId!),
    enabled: Boolean(customerId),
    ...b2bNavigationQueryOptions,
  });
}
